/* eslint react-hooks/rules-of-hooks: "warn" */
import React, { type FunctionComponent, useState } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { spacing } from 'gin-and-tonic/foundation/spacing'
import { useDashMessage } from 'intl/intl.hooks'
import { usePartnerSandboxOrderSimulateEvent } from 'domains/connect/api/sandbox-order/partnerSandboxOrderSimulateEvent.hooks'
import { Button } from 'gin-and-tonic/common/button/Button'
import { Input } from 'gin-and-tonic/common/input/Input'
import LoadingDots from 'legacy/components/LoadingDots'
import {
  type SandboxOrder,
  type SandboxOrderSimulationEvent,
} from 'domains/connect/components/fulfillment-simulator/utils/types'
import { checkboxItemStyle } from 'domains/connect/components/styles'
import { useErrorNotification } from 'domains/connect/components/utils/notifications.hooks'

interface Props {
  sandboxOrder?: SandboxOrder
  simulationEvent?: SandboxOrderSimulationEvent
  onSubmit?: () => void
}

const fieldRowStyle = css`
  display: grid;
  grid-template-columns: 220px 400px;
`

const labelStyle = css`
  text-align: right;
  font-weight: 600;
  padding-right: ${spacing.X12};
  padding-top: ${spacing.X20};
`

const valueStyle = css`
  margin-left: ${spacing.X16};
  margin-bottom: ${spacing.X12};
  padding-right: ${spacing.X12};
  padding-top: ${spacing.X20};
`

const loadingDotsStyle = css`
  padding: ${spacing.X128} 0;
`

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: ${spacing.X20} auto;
  width: 90%;
`

interface DynamicArgumentState {
  argument: string
  value: string
  setter: (value: string) => void
}

export const SimulateOrderEventForm: FunctionComponent<Props> = ({
  sandboxOrder,
  simulationEvent,
  onSubmit,
}) => {
  const {
    simulateSandboxOrderEvent,
    loading: simulateOrderEventLoading,
    error: simulateOrderEventError,
  } = usePartnerSandboxOrderSimulateEvent(sandboxOrder.id)
  useErrorNotification(simulateOrderEventError, 'connect.fulfillmentSimulator.simulateEvent.error')
  const simulationArguments = simulationEvent.arguments
  const callbackEvent = simulationEvent.outcome.callback
  const [sendCallbackEvent, setSendCallbackEvent] = useState<boolean>(false)
  const simulationArgState: DynamicArgumentState[] = simulationArguments.map(argument => {
    const [value, setter] = useState('')
    return {
      argument,
      value,
      setter,
    }
  })

  const simulationArgumentInputs = simulationArgState.map(({ argument, value, setter }) => (
    <div key={argument} css={fieldRowStyle}>
      <span css={labelStyle}>{argument}</span>
      <Input
        placeholder={argument}
        name={argument}
        value={value}
        onChange={e => setter(e.target.value)}
      />
    </div>
  ))

  const onFormSubmit = () => {
    // TODO: validate args
    const simulationArguments = {
      simulationEvent: simulationEvent.event,
      simulationEventArgs: simulationArgState.map(({ value }) => value),
    }

    if (sendCallbackEvent) {
      simulationArguments['callbackEvent'] = callbackEvent
    }

    simulateSandboxOrderEvent(simulationArguments).then(() => onSubmit())
  }

  const handleSendCallbackEventChange = event => {
    setSendCallbackEvent(event.target.checked)
  }

  const messages = {
    simulationEvent: useDashMessage(
      'connect.fulfillmentSimulator.orderProps.simulationEvents.event'
    ),
    simulateEventButton: useDashMessage('connect.fulfillmentSimulator.simulateEvent'),
    callbackEvent: useDashMessage(
      'connect.fulfillmentSimulator.orderProps.simulationEvents.callbackEventName'
    ),
    triggerCallback: useDashMessage(
      'connect.fulfillmentSimulator.orderProps.simulationEvents.triggerCallbackEvent'
    ),
  }

  if (simulateOrderEventLoading) {
    return (
      <div css={loadingDotsStyle}>
        <LoadingDots centered />
      </div>
    )
  }
  return (
    <Container>
      <div css={fieldRowStyle}>
        <span css={labelStyle}>{messages.simulationEvent}</span>
        <span css={valueStyle}>{simulationEvent.event}</span>
      </div>

      {simulationArgumentInputs}

      <div css={fieldRowStyle}>
        <span css={labelStyle}>{messages.callbackEvent}</span>
        <span css={valueStyle}>{callbackEvent}</span>
      </div>

      <div css={fieldRowStyle}>
        <span css={labelStyle}>{messages.triggerCallback}</span>
        <span css={valueStyle}>
          <div css={checkboxItemStyle}>
            <input
              type="checkbox"
              name="callbackEvent"
              checked={sendCallbackEvent}
              onChange={handleSendCallbackEventChange}
            />
          </div>
        </span>
      </div>

      <Button onClick={onFormSubmit}>{messages.simulateEventButton}</Button>
    </Container>
  )
}
